import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Calculator, RotateCcw, Palette, ChevronDown, Info, Minus, Plus, PaintBucket } from "lucide-react";
import { Reveal } from "./components";

const SURFACES = [
  { key: "walls", label: "Walls only" },
  { key: "ceiling", label: "Ceiling only" },
  { key: "both", label: "Walls and ceiling" },
];

const DEFAULTS = {
  length: "",
  width: "",
  height: "8",
  doors: "0",
  windows: "0",
  coats: 2,
  surface: "walls",
  coverage: "350",
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

function Field({ label, id, children, hint }) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-xs uppercase tracking-[0.14em] font-semibold text-ink/70">{label}</label>
      {children}
      {hint && <span className="text-xs text-ink/45">{hint}</span>}
    </div>
  );
}

function Stepper({ id, value, onChange, min = 0, step = 1, suffix, testid }) {
  const set = (next) => onChange(String(Math.max(min, next)));
  const current = num(value);
  return (
    <div className="flex items-stretch border border-black/15 bg-white">
      <button
        type="button"
        aria-label={`Decrease ${id}`}
        data-testid={`${testid}-minus`}
        onClick={() => set(current - step)}
        className="w-12 shrink-0 flex items-center justify-center text-ink/60 hover:bg-ink hover:text-white transition-colors"
      >
        <Minus size={16} />
      </button>
      <div className="flex-1 flex items-center justify-center gap-1 border-x border-black/10">
        <input
          id={id}
          type="number"
          min={min}
          step={step}
          inputMode="decimal"
          value={value}
          data-testid={testid}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") return onChange("");
            onChange(String(Math.max(min, num(v))));
          }}
          className="w-full text-center py-3 text-base bg-transparent outline-none focus:text-gold"
        />
        {suffix && <span className="text-xs text-ink/40 pr-3">{suffix}</span>}
      </div>
      <button
        type="button"
        aria-label={`Increase ${id}`}
        data-testid={`${testid}-plus`}
        onClick={() => set(current + step)}
        className="w-12 shrink-0 flex items-center justify-center text-ink/60 hover:bg-ink hover:text-white transition-colors"
      >
        <Plus size={16} />
      </button>
    </div>
  );
}

function purchaseBreakdown(gallons) {
  if (gallons <= 0) return [];
  const buckets = Math.floor(gallons / 5);
  const singles = gallons % 5;
  const parts = [];
  if (buckets > 0) parts.push(`${buckets} × 5-gallon bucket${buckets > 1 ? "s" : ""}`);
  if (singles > 0) parts.push(`${singles} × 1-gallon can${singles > 1 ? "s" : ""}`);
  return parts;
}

export function PaintCalculator() {
  const [form, setForm] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const compute = useCallback(() => {
    const length = num(form.length);
    const width = num(form.width);
    if (length <= 0 || width <= 0) {
      setResult(null);
      setError("Please enter a room length and width greater than zero.");
      return false;
    }
    setError("");

    const height = num(form.height) || 8;
    const doors = num(form.doors);
    const windows = num(form.windows);
    const coats = form.coats || 1;
    const coverage = num(form.coverage) || 350;
    const doWalls = form.surface === "walls" || form.surface === "both";
    const doCeiling = form.surface === "ceiling" || form.surface === "both";

    let wallArea = 0;
    if (doWalls) {
      const perimeter = 2 * length + 2 * width;
      wallArea = perimeter * height - doors * 20 - windows * 15;
      wallArea = Math.max(0, wallArea);
    }
    const ceilingArea = doCeiling ? length * width : 0;

    const gallonsWalls = doWalls ? Math.ceil((wallArea * coats) / coverage) : 0;
    const gallonsCeiling = doCeiling ? Math.ceil((ceilingArea * coats) / coverage) : 0;
    const totalGallons = gallonsWalls + gallonsCeiling;

    setResult({
      paintable: Math.round(wallArea + ceilingArea),
      coats,
      doWalls,
      doCeiling,
      gallonsWalls,
      gallonsCeiling,
      totalGallons,
      breakdown: purchaseBreakdown(totalGallons),
    });
    return true;
  }, [form]);

  // Instant recalculation after the first successful calculation.
  useEffect(() => {
    if (result) compute();
  }, [form]);

  const onCalculate = () => compute();
  const onReset = () => {
    setForm(DEFAULTS);
    setResult(null);
    setError("");
    setShowAdvanced(false);
  };

  return (
    <div data-testid="calculator-page" className="pt-32 bg-ivory min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <Reveal>
          <p className="eyebrow text-gold">Paint Calculator</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">How Much Paint Do I Need?</h1>
          <p className="text-muted-foreground mt-5 max-w-2xl leading-relaxed">
            Estimate how much paint you&apos;ll need for your room in less than a minute. For the most accurate result, enter the room measurements and select the number of coats.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mt-12 items-start">
          {/* INPUTS */}
          <Reveal>
            <div data-testid="calculator-inputs" className="bg-white border border-black/10 shadow-[0_24px_60px_-32px_rgba(0,0,0,0.25)] p-6 sm:p-9">
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Room length" id="calc-length">
                  <div className="flex items-center border border-black/15 bg-white">
                    <input id="calc-length" data-testid="input-length" type="number" min="0" inputMode="decimal" placeholder="e.g. 14"
                      value={form.length}
                      onChange={(e) => update("length", e.target.value === "" ? "" : String(Math.max(0, num(e.target.value))))}
                      className="w-full px-4 py-3 text-base bg-transparent outline-none focus:text-gold" />
                    <span className="text-xs text-ink/40 pr-4">ft</span>
                  </div>
                </Field>
                <Field label="Room width" id="calc-width">
                  <div className="flex items-center border border-black/15 bg-white">
                    <input id="calc-width" data-testid="input-width" type="number" min="0" inputMode="decimal" placeholder="e.g. 12"
                      value={form.width}
                      onChange={(e) => update("width", e.target.value === "" ? "" : String(Math.max(0, num(e.target.value))))}
                      className="w-full px-4 py-3 text-base bg-transparent outline-none focus:text-gold" />
                    <span className="text-xs text-ink/40 pr-4">ft</span>
                  </div>
                </Field>
                <Field label="Ceiling height" id="calc-height">
                  <Stepper id="calc-height" testid="input-height" value={form.height} onChange={(v) => update("height", v)} min={0} step={1} suffix="ft" />
                </Field>
                <Field label="Number of coats" id="calc-coats">
                  <div className="grid grid-cols-3 gap-2" role="group" aria-label="Number of coats">
                    {[1, 2, 3].map((c) => (
                      <button key={c} type="button" data-testid={`coats-${c}`} onClick={() => update("coats", c)}
                        className={`py-3 text-sm font-semibold border transition-colors ${form.coats === c ? "bg-ink text-white border-ink" : "bg-white text-ink border-black/15 hover:border-ink"}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Doors" id="calc-doors">
                  <Stepper id="calc-doors" testid="input-doors" value={form.doors} onChange={(v) => update("doors", v)} min={0} step={1} />
                </Field>
                <Field label="Standard windows" id="calc-windows">
                  <Stepper id="calc-windows" testid="input-windows" value={form.windows} onChange={(v) => update("windows", v)} min={0} step={1} />
                </Field>
              </div>

              <div className="mt-6">
                <Field label="What are you painting?" id="calc-surface">
                  <div className="grid sm:grid-cols-3 gap-2" role="group" aria-label="What are you painting">
                    {SURFACES.map((s) => (
                      <button key={s.key} type="button" data-testid={`surface-${s.key}`} onClick={() => update("surface", s.key)}
                        className={`py-3 px-2 text-sm font-medium border transition-colors ${form.surface === s.key ? "bg-gold text-white border-gold" : "bg-white text-ink border-black/15 hover:border-gold"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>

              {/* ADVANCED */}
              <div className="mt-6 border-t border-black/10 pt-5">
                <button type="button" data-testid="advanced-toggle" onClick={() => setShowAdvanced((v) => !v)}
                  className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold text-ink/70 hover:text-gold transition-colors">
                  Advanced options
                  <ChevronDown size={15} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
                </button>
                {showAdvanced && (
                  <div className="mt-4" data-testid="advanced-panel">
                    <Field label="Paint coverage" id="calc-coverage" hint="Square feet covered per gallon. Most paints cover about 350 sq ft.">
                      <div className="flex items-center border border-black/15 bg-white max-w-xs">
                        <input id="calc-coverage" data-testid="input-coverage" type="number" min="1" inputMode="decimal"
                          value={form.coverage}
                          onChange={(e) => update("coverage", e.target.value === "" ? "" : String(Math.max(0, num(e.target.value))))}
                          className="w-full px-4 py-3 text-base bg-transparent outline-none focus:text-gold" />
                        <span className="text-xs text-ink/40 pr-4 whitespace-nowrap">sq ft / gal</span>
                      </div>
                    </Field>
                  </div>
                )}
              </div>

              {error && <p data-testid="calculator-error" className="text-sm text-red-600 mt-5">{error}</p>}

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <button type="button" data-testid="calculate-btn" onClick={onCalculate}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-ink text-white overline px-8 py-4 hover:bg-gold transition-colors">
                  <Calculator size={16} /> Calculate My Paint
                </button>
                <button type="button" data-testid="reset-btn" onClick={onReset}
                  className="inline-flex items-center justify-center gap-2 border border-ink text-ink overline px-8 py-4 hover:bg-ink hover:text-white transition-colors">
                  <RotateCcw size={15} /> Start Over
                </button>
              </div>
            </div>
          </Reveal>

          {/* RESULTS */}
          <Reveal delay={0.1}>
            <div className="lg:sticky lg:top-28">
              {!result ? (
                <div data-testid="calculator-placeholder" className="bg-ink text-white p-9 md:p-12 flex flex-col items-start justify-center min-h-[420px]">
                  <PaintBucket size={40} className="text-gold" />
                  <h2 className="font-serif text-3xl md:text-4xl mt-6 leading-tight">Your estimate appears here</h2>
                  <p className="text-white/70 mt-4 leading-relaxed max-w-sm">Enter your room measurements and press Calculate My Paint to see exactly how much paint your project needs.</p>
                </div>
              ) : (
                <div data-testid="calculator-results" className="bg-ink text-white p-8 md:p-11">
                  <p className="eyebrow text-gold">Your Estimate</p>
                  <div className="mt-6 flex items-end gap-3">
                    <span data-testid="result-total-gallons" className="font-serif text-6xl md:text-7xl leading-none text-white">{result.totalGallons}</span>
                    <span className="text-white/70 mb-2 text-lg">{result.totalGallons === 1 ? "gallon" : "gallons"} total</span>
                  </div>

                  <div className="grid grid-cols-2 gap-px bg-white/15 mt-8">
                    <div className="bg-ink p-4">
                      <p className="text-xs uppercase tracking-[0.14em] text-white/50">Paintable area</p>
                      <p data-testid="result-paintable" className="font-serif text-2xl mt-1">{result.paintable.toLocaleString()} <span className="text-sm text-white/60">sq ft</span></p>
                    </div>
                    <div className="bg-ink p-4">
                      <p className="text-xs uppercase tracking-[0.14em] text-white/50">Coats</p>
                      <p data-testid="result-coats" className="font-serif text-2xl mt-1">{result.coats}</p>
                    </div>
                    {result.doWalls && (
                      <div className="bg-ink p-4">
                        <p className="text-xs uppercase tracking-[0.14em] text-white/50">Walls</p>
                        <p data-testid="result-walls" className="font-serif text-2xl mt-1">{result.gallonsWalls} <span className="text-sm text-white/60">gal</span></p>
                      </div>
                    )}
                    {result.doCeiling && (
                      <div className="bg-ink p-4">
                        <p className="text-xs uppercase tracking-[0.14em] text-white/50">Ceiling</p>
                        <p data-testid="result-ceiling" className="font-serif text-2xl mt-1">{result.gallonsCeiling} <span className="text-sm text-white/60">gal</span></p>
                      </div>
                    )}
                  </div>

                  <div className="mt-8 border-t border-white/15 pt-6">
                    <p className="text-xs uppercase tracking-[0.14em] text-gold">Suggested purchase</p>
                    <ul data-testid="result-breakdown" className="mt-3 space-y-1.5">
                      {result.breakdown.length === 0 ? (
                        <li className="text-white/80">No paint required for these inputs.</li>
                      ) : (
                        result.breakdown.map((b) => (
                          <li key={b} className="flex items-center gap-2 text-white/90"><PaintBucket size={15} className="text-gold shrink-0" /> {b}</li>
                        ))
                      )}
                    </ul>
                  </div>

                  <p data-testid="result-disclaimer" className="text-xs text-white/50 leading-relaxed mt-8 flex gap-2">
                    <Info size={14} className="text-white/40 shrink-0 mt-0.5" />
                    Paint coverage varies by surface, texture, application method, product, and color change. We recommend purchasing a little extra for touch-ups.
                  </p>
                </div>
              )}

              {/* CTA */}
              <div data-testid="calculator-cta" className="bg-white border border-black/10 p-7 md:p-8 mt-6">
                <p className="font-serif text-2xl leading-snug">Need help choosing the right color?</p>
                <p className="text-muted-foreground text-sm mt-2">Explore our curated paint palettes and let our in-house designer guide you.</p>
                <Link to="/paints" data-testid="cta-palettes-link" className="inline-flex items-center gap-2 bg-gold text-white overline px-7 py-3.5 mt-5 hover:bg-gold-dark transition-colors">
                  <Palette size={16} /> Explore Our Paint Palettes
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
